// src/components/resources/tools/generators/AdSafeZone/image-dimensions.ts

/**
 * @fileoverview Byte-level image format detection + dimension parsing.
 *
 * Why this exists (`r3564283755`, `r3564513498`-family): the 48 MP pixel
 * budget originally ran in `Image.onload` — i.e. AFTER the browser had
 * already decoded the bitmap, so a highly compressed sub-10 MB "pixel bomb"
 * (e.g. a 20000×20000 flat-color PNG) still triggered the freeze/OOM the
 * budget exists to prevent. Uploads are capped at 10 MB, so callers read the
 * WHOLE file into memory (cheap next to a decode) and validate from bytes:
 *
 * 1. {@link detectImageFormat} — magic-byte format identification. Trust the
 *    bytes, not `File.type`: browsers may report an empty MIME string for a
 *    perfectly valid file, and a mislabeled extension lies in the other
 *    direction.
 * 2. {@link readImageDimensionsFromHeader} — dimension parse. Over a COMPLETE
 *    well-formed PNG/JPEG/WebP the marker is always findable (PNG IHDR and
 *    WebP's first chunk are at fixed offsets; a JPEG must declare SOF before
 *    its entropy-coded data), so a `null` from a byte stream that passed
 *    format detection means the file is malformed for our supported set —
 *    callers reject it BEFORE decode rather than "decode and hope".
 *
 * Pure, DOM-free, and unit-tested against hand-built fixtures
 * (`__tests__/image-dimensions.test.ts`).
 */

export interface ImageDimensions {
  width: number
  height: number
}

/** Image formats the checker accepts (mirrors the resource config). */
export type SupportedImageFormat = 'png' | 'jpeg' | 'webp'

// Every caller length-checks the buffer before invoking these readers, so an
// in-bounds `Uint8Array` element is always a `number` at runtime; the `?? 0`
// only satisfies `noUncheckedIndexedAccess` (which types indexed access as
// `number | undefined`) and is never exercised on the validated path — an
// undefined byte would coerce to 0 in the bit ops anyway, so behaviour is
// unchanged versus the prior non-null assertions.
const readU32BE = (b: Uint8Array, offset: number): number =>
  (((b[offset] ?? 0) << 24) | ((b[offset + 1] ?? 0) << 16) | ((b[offset + 2] ?? 0) << 8) | (b[offset + 3] ?? 0)) >>>
  0

const readU24LE = (b: Uint8Array, offset: number): number =>
  (b[offset] ?? 0) | ((b[offset + 1] ?? 0) << 8) | ((b[offset + 2] ?? 0) << 16)

const readU16BE = (b: Uint8Array, offset: number): number => ((b[offset] ?? 0) << 8) | (b[offset + 1] ?? 0)

const readU16LE = (b: Uint8Array, offset: number): number => (b[offset] ?? 0) | ((b[offset + 1] ?? 0) << 8)

const ascii = (b: Uint8Array, offset: number, text: string): boolean => {
  for (let i = 0; i < text.length; i++) {
    if (b[offset + i] !== text.charCodeAt(i)) return false
  }
  return true
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

const hasPngSignature = (b: Uint8Array): boolean =>
  b.length >= PNG_SIGNATURE.length && PNG_SIGNATURE.every((byte, i) => b[i] === byte)

const hasJpegSignature = (b: Uint8Array): boolean =>
  b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff

const hasWebpSignature = (b: Uint8Array): boolean =>
  b.length >= 12 && ascii(b, 0, 'RIFF') && ascii(b, 8, 'WEBP')

/**
 * Identify a supported image format from its magic bytes. This is the
 * format gate — `File.type` is advisory only (it can be empty for a valid
 * file, or mislabeled), so callers validate the BYTES and never the MIME
 * string. Returns `null` for anything that is not PNG/JPEG/WebP.
 */
export function detectImageFormat(bytes: Uint8Array): SupportedImageFormat | null {
  if (hasPngSignature(bytes)) return 'png'
  if (hasJpegSignature(bytes)) return 'jpeg'
  if (hasWebpSignature(bytes)) return 'webp'
  return null
}

/** PNG: 8-byte signature, then the IHDR chunk carries width/height big-endian. */
function parsePng(b: Uint8Array): ImageDimensions | null {
  if (b.length < 24) return null
  if (!hasPngSignature(b)) return null
  if (!ascii(b, 12, 'IHDR')) return null
  return { width: readU32BE(b, 16), height: readU32BE(b, 20) }
}

/** JPEG SOF markers that carry frame dimensions (C0–CF minus DHT/JPG/DAC). */
const JPEG_SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
])

/**
 * JPEG: walk the marker segments until a Start-Of-Frame; its payload is
 * [length][precision][height u16 BE][width u16 BE].
 */
function parseJpeg(b: Uint8Array): ImageDimensions | null {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null
  let pos = 2
  while (pos + 9 <= b.length) {
    if (b[pos] !== 0xff) return null // lost sync — not a well-formed stream
    // Skip fill bytes (spec allows repeated 0xFF before a marker).
    while (pos + 1 < b.length && b[pos + 1] === 0xff) pos++
    // A run of fill bytes to end-of-buffer leaves no marker byte: that is a
    // truncated/malformed stream for our supported set, so return null (the
    // documented "malformed → null" contract) rather than assert non-null.
    const marker = b[pos + 1]
    if (marker === undefined) return null
    if (JPEG_SOF_MARKERS.has(marker)) {
      if (pos + 9 > b.length) return null
      return { width: readU16BE(b, pos + 7), height: readU16BE(b, pos + 5) }
    }
    // SOS (entropy-coded data) or EOI without a SOF: give up.
    if (marker === 0xda || marker === 0xd9) return null
    const segmentLength = readU16BE(b, pos + 2)
    if (segmentLength < 2) return null
    pos += 2 + segmentLength
  }
  return null
}

/**
 * WebP: RIFF container, dimensions in the FIRST chunk — VP8X (extended,
 * 24-bit LE minus-one fields), VP8 (lossy, 14-bit LE fields after the frame
 * tag + sync code), or VP8L (lossless, packed 14-bit minus-one fields).
 */
function parseWebp(b: Uint8Array): ImageDimensions | null {
  if (b.length < 30 || !ascii(b, 0, 'RIFF') || !ascii(b, 8, 'WEBP')) return null
  if (ascii(b, 12, 'VP8X')) {
    return { width: readU24LE(b, 24) + 1, height: readU24LE(b, 27) + 1 }
  }
  if (ascii(b, 12, 'VP8 ')) {
    // Chunk data at 20: 3-byte frame tag, then the 9D 01 2A sync code.
    if (b[23] !== 0x9d || b[24] !== 0x01 || b[25] !== 0x2a) return null
    return { width: readU16LE(b, 26) & 0x3fff, height: readU16LE(b, 28) & 0x3fff }
  }
  if (ascii(b, 12, 'VP8L')) {
    if (b[20] !== 0x2f) return null
    // Indices 21–24 are in-bounds here (length ≥ 30 guard above); `?? 0` only
    // satisfies noUncheckedIndexedAccess and never fires on the valid path.
    const bits = (b[21] ?? 0) | ((b[22] ?? 0) << 8) | ((b[23] ?? 0) << 16) | ((b[24] ?? 0) << 24)
    return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 }
  }
  return null
}

/**
 * Read pixel dimensions from the header bytes of a PNG / JPEG / WebP file.
 * Returns `null` when the format is unrecognized or the header is truncated —
 * callers must treat `null` as "unknown" and fall back to their decode-time
 * check, never as "safe".
 */
export function readImageDimensionsFromHeader(bytes: Uint8Array): ImageDimensions | null {
  const dims = parsePng(bytes) ?? parseJpeg(bytes) ?? parseWebp(bytes)
  if (!dims) return null
  if (!Number.isFinite(dims.width) || !Number.isFinite(dims.height)) return null
  if (dims.width <= 0 || dims.height <= 0) return null
  return dims
}
